import { useEffect, useRef, useState } from 'react';

interface UseCAPAutoSaveOptions<T> {
    data: T | null;
    onSave: (data: T) => Promise<void>;
    idleTimeout?: number;
}

interface UseCAPAutoSaveReturn<T> {
    updateField: (field: keyof T, value: any) => void;
    autoSaveStatus: 'idle' | 'saving' | 'saved';
    hasChanges: boolean;
}

export const useCAPAutoSave = <T extends object>({
    data,
    onSave,
    idleTimeout = 15000,
}: UseCAPAutoSaveOptions<T>): UseCAPAutoSaveReturn<T> => {
    const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hasChangesRef = useRef(false);
    const originalDataRef = useRef<T | null>(null);
    const currentDataRef = useRef<T | null>(null);
    const [isIdle, setIsIdle] = useState(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const [hasChanges, setHasChanges] = useState(false);

    // store original when data loads
    useEffect(() => {
        if (data) {
            originalDataRef.current = data;
            currentDataRef.current = data;
            hasChangesRef.current = false;
            setHasChanges(false);
        }
    }, [data]);

    // update a single field
    const updateField = (field: keyof T, value: any) => {
        if (!currentDataRef.current) return;

        const updated = { ...currentDataRef.current, [field]: value };
        currentDataRef.current = updated;

        const changed = JSON.stringify(updated) !== JSON.stringify(originalDataRef.current);
        hasChangesRef.current = changed;
        setHasChanges(changed);

        resetIdleTimer();
    };

    // auto save logic
    const handleAutoSave = async () => {
        if (!hasChangesRef.current || !currentDataRef.current) return;

        try {
            setAutoSaveStatus('saving');
            await onSave(currentDataRef.current);

            originalDataRef.current = currentDataRef.current;
            hasChangesRef.current = false;
            setHasChanges(false);
            setAutoSaveStatus('saved');

            setTimeout(() => setAutoSaveStatus('idle'), 2000);
        } catch (error) {
            console.error('Auto-save failed:', error);
            setAutoSaveStatus('idle');
        }
    };

    // trigger auto save when idle and has changes
    useEffect(() => {
        if (isIdle && hasChangesRef.current) {
            handleAutoSave();
        }
    }, [isIdle]);

    // idle detection
    const resetIdleTimer = () => {
        setIsIdle(false);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => setIsIdle(true), idleTimeout);
    };

    useEffect(() => {
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
        events.forEach((event) => window.addEventListener(event, resetIdleTimer));
        resetIdleTimer();

        return () => {
            events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        };
    }, []);

    return { updateField, autoSaveStatus, hasChanges };
};